import { IconButton, TextField, Tooltip, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import { useApp } from 'contexts/App/appContext'
import { useFlow } from 'contexts/Flow/flowContext'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { GetBotsGroups } from 'services/BotService'
import {
  GetGroupIsLiberated,
  ReleaseGroupAccess,
  UpdateLastAccess,
} from 'services/FlowService'
import {
  AutoCompleteOption,
  AutoCompleteWrap,
  BotAndGroupInfo,
  ImportExportIcons,
  StyledMttButtonPurple,
} from '../styles'

import { Autocomplete } from '@material-ui/lab'
import { createFilterOptions } from '@material-ui/lab/Autocomplete'
import { localStore } from 'utils/Stores'

import { CloudDownload, CloudUpload } from '@material-ui/icons'

import { useBuilder } from 'contexts/Builder'
import { useFintalkCloud } from 'contexts/FintalkCloud/fintalkCloudContext'
import { usePermissions } from 'contexts/Permissions/permissionsContext'
import { ActionPermissions } from 'contexts/Permissions/types'
import { useToast } from 'contexts/ToastContext'
import { focusNodeByName } from 'components/Flow/utils/NodeContainer'
import { GetFlexBlocks } from 'services/FlexBlocksService'
import useTranslator from 'utils/hooks/Translator'
import { exportGroup, importGroup } from 'utils/ImportExportGroup'

const RenderBots = () => {
  const { currentData } = useFintalkCloud()
  const focusGeneration = useRef(0)
  const { toastNotification } = useToast()
  const { dispatch: builderDisptach } = useBuilder()
  const {
    dispatch,
    saveFlow,
    state,
    loadFlow,
    mountNodeContent,
    stopEditing,
    setIsConfirmExitOpen,
    editingNodeId,
    setEditingNodeId,
  } = useFlow()

  const { hasPermission } = usePermissions()
  const dispatchApp = useApp().dispatch

  const { groupsNames } = state
  const [groupsNamesLocal, setGroupsNamesLocal] = useState<string[]>([])
  const defaultFilterOptions = createFilterOptions<any>()

  const { getTranslation } = useTranslator()
  const closeTranslated = getTranslation('close')
  const openTranslated = getTranslation('open')
  const noResultsTranslated = getTranslation('noResults')
  const { projectName, companyName, agentName, groupName, blockName } =
    currentData
  const botName = agentName

  const getAgentData = useCallback(
    async (agent: string) => {
      await releaseGroup()

      if (!agent) return

      dispatch({ type: 'updateForm', data: { loading: true } })

      const response = await GetBotsGroups({ bot_name: agent }, dispatchApp)

      let groups = response.Data.data

      if (!response.Success) {
        toastNotification({
          type: 'error',
          message: getTranslation('toast.error.error'),
        })

        console.log('Erro ao buscar grupos do bot')

        dispatch({ type: 'updateForm', data: { loading: false } })

        return
      }

      if (state.isEditing) stopEditing()

      if (Array.isArray(groups)) {
        const idGroup =
          groups.find((group) => group === groupName) || 'principal'

        const isFlowRead = hasPermission({
          company: companyName || '',
          agent,
          permissions: currentData.permissions,
          actionPermissions: ActionPermissions.READER,
        })

        if (idGroup && isFlowRead) {
          dispatch({ type: 'updateForm', data: { loading: true } })
          const loaded = await loadFlow(idGroup, agent)
          localStorage.setItem('@studio:group', idGroup)
          window.parent.postMessage(
            { type: 'BUILDER_GROUP', data: { groupName: idGroup } },
            '*',
          )

          if (loaded && blockName && state.editorFlow) {
            const editor = state.editorFlow
            const generation = ++focusGeneration.current

            focusNodeByName(editor, blockName).then((found) => {
              if (found || generation !== focusGeneration.current) return

              toastNotification({
                type: 'warning',
                message: getTranslation('toast.warning.blockNotFound', {
                  block: blockName,
                  group: idGroup,
                }),
              })
            })
          }
        } else {
          state.editorFlow?.clear()
        }

        localStorage.setItem('@studio:agent', agent)

        dispatch({
          type: 'updateForm',
          data: {
            companyName,
            botName: agent,
            idGroup,
            groupsNames: groups,
            loading: false,
          },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyName, dispatch, dispatchApp, groupName, loadFlow, state.editorFlow],
  )

  useEffect(() => {
    async function initialize() {
      if (!state.editorFlow) return

      // Load group flow and check for errors
      await getAgentData(botName)
    }

    initialize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, projectName, state.editorFlow])

  const isFlowWrite = hasPermission({
    company: currentData.companyName,
    agent: currentData.agentName,
    permissions: currentData.permissions,
    actionPermissions: ActionPermissions.WRITE,
  })

  useEffect(() => {
    setGroupsNamesLocal(groupsNames.map((group) => group))
  }, [groupsNames])

  const fixGroupsNamesOrder = useCallback(() => {
    const curGroups = groupsNamesLocal

    const principalIndex = curGroups.findIndex((group) => group === 'principal')
    const principal = curGroups[principalIndex]

    if (principalIndex >= 0) {
      curGroups.splice(principalIndex, 1)
      setGroupsNamesLocal([
        principal,
        ...curGroups.map((group) => group).sort(),
      ])
    } else {
      setGroupsNamesLocal(curGroups.map((group) => group).sort())
    }
  }, [groupsNamesLocal])

  const releaseGroup = async () => {
    if (currentData.agentName && currentData.groupName && state.isEditing) {
      await ReleaseGroupAccess(
        {
          bot_name: currentData.agentName,
          groupName: currentData.groupName,
        },
        dispatchApp,
      )
    }

    state.botsUrls.urlFacebook = ''
    state.botsUrls.urlFalazap = ''
    state.botsUrls.urlTelegram = ''
    state.botsUrls.urlTwitter = ''
    state.botsUrls.urlWhatsApp = ''
  }

  const handleChangeGroup = async (
    e: React.ChangeEvent<{
      name?: string | undefined
      textContent: unknown
    }>,
  ) => {
    if (!e || !e.target.textContent || !state.editorFlow) return

    if (editingNodeId) {
      console.log('editingNodeId', editingNodeId)
      setIsConfirmExitOpen(true)
      return
    }

    const idGroup = String(e.target.textContent)

    await changeGroupFlow(idGroup)

    setIsConfirmExitOpen(false)
    setEditingNodeId(null)
  }

  const changeGroupFlow = async (group_id: string, deleteGroup = false) => {
    dispatch({ type: 'updateForm', data: { loading: true } })

    if (state.isEditing) {
      !deleteGroup && (await updateFlow())

      await ReleaseGroupAccess(
        {
          bot_name: botName || '',
          groupName: state.idGroup || '',
        },
        dispatchApp,
      )
    }

    await loadFlow(group_id, botName || '', false, deleteGroup)

    localStorage.setItem('@studio:group', group_id)

    dispatch({
      type: 'updateForm',
      data: { idGroup: group_id, loading: false },
    })
  }

  const updateLastAccess = async () => {
    const groupLastVersion = localStore.get('groupLastVersion') || ''

    if (
      !!state.editorFlow &&
      state.isEditing &&
      JSON.stringify(state.editorFlow.drawflow) !== groupLastVersion
    ) {
      const sBotAndGroup = localStore.get('current_bot_group')

      if (sBotAndGroup) {
        const { botName, groupName } = JSON.parse(sBotAndGroup)

        if (botName && groupName) {
          await UpdateLastAccess(
            {
              bot_name: botName,
              groupName,
            },
            dispatchApp,
          )
        }
      }
    }
  }

  const updateFlow = async () => {
    await updateLastAccess()
    await saveFlow()
  }

  function handleAddNewGroup() {
    if (botName) {
      dispatch({ type: 'openModalCreateGroup' })
    } else {
      toastNotification({
        type: 'warning',
        message: '*Selecione um bot para criar uma grupo!',
      })
    }
  }

  const handleOpenModalDeleteGroup = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    group: string,
  ) => {
    e.stopPropagation()

    if (botName) {
      const isFlexGroup = group.startsWith('flex-')
      if (isFlexGroup) {
        const flexBlocksResponse = await GetFlexBlocks(
          state.botName!,
          dispatchApp,
        )

        if (flexBlocksResponse.Data.data) {
          const flexBlocks = flexBlocksResponse.Data.data
          const flexBlockBeingDeleted = flexBlocks.flex_blocks.find(
            (block) => `flex-${block.block_name}` === group,
          )
          if (
            flexBlockBeingDeleted &&
            flexBlockBeingDeleted.groups_used.length > 0
          ) {
            const groups = flexBlockBeingDeleted.groups_used.map(
              (group) => group.group_name,
            )

            toastNotification({
              type: 'error',
              message: `Não foi possível apagar esse grupo. Blocos Flex referentes a ele estão em uso no(s) grupo(s) "${groups}".`,
            })
            return
          }
        }
      }

      const response = await GetGroupIsLiberated(
        {
          bot_name: botName,
          groupName: group,
        },
        dispatchApp,
      )
      if (!response.Data.group_is_liberated) {
        toastNotification({
          type: 'error',
          message: getTranslation('toast.error.agentOrGroupInUse', {
            object: getTranslation('group'),
            user: response.Data.current_user,
            status: getTranslation('agentOrGroupStatus.removed'),
          }),
        })
      } else {
        const selectedGroup = groupsNames.find((newGroup) => newGroup === group)
        if (selectedGroup)
          builderDisptach({
            type: 'setGroupToDelete',
            data: { groupToDelete: { id: selectedGroup, name: selectedGroup } },
          })
      }
    }
  }

  const importFileDefault = () => {
    const button = document.getElementById('import-button') as HTMLButtonElement
    if (button) {
      const disabled = button.disabled

      if (!disabled) {
        importGroup(state, toastNotification, mountNodeContent, {
          invalidFileForGroup: getTranslation(
            'toast.error.invalidFileForGroup',
          ),
          invalidFileForPrincipal: getTranslation(
            'toast.error.invalidFileForPrincipal',
          ),
          onlyValidForPrincipal: getTranslation(
            'toast.error.onlyValidForPrincipal',
          ),
        })
      }
    }
  }

  const exportFileDefault = () => {
    exportGroup(state)
  }

  return (
    <>
      <BotAndGroupInfo>
        <Typography variant="body1">{getTranslation('agentGroup')}</Typography>

        <AutoCompleteWrap>
          <Autocomplete
            id="group-autocomplete"
            style={{
              width: '100%',
            }}
            disabled={!currentData.agentName}
            onOpen={fixGroupsNamesOrder}
            options={groupsNamesLocal}
            value={currentData.groupName}
            noOptionsText={noResultsTranslated}
            onChange={(event: any) => {
              const addGroup = getTranslation('addGroup')
              if (event.target.textContent.trim() !== addGroup) {
                handleChangeGroup(event)
              }
            }}
            renderOption={(option) =>
              !option || typeof option !== 'string' ? (
                option
              ) : (
                <React.Fragment>
                  <AutoCompleteOption>{option}</AutoCompleteOption>
                  {option !== 'principal' && (
                    <>
                      {isFlowWrite && (
                        <button
                          style={{
                            margin: 4,
                            border: 'none',
                            backgroundColor: 'transparent',
                          }}
                          onClick={(e) => handleOpenModalDeleteGroup(e, option)}
                        >
                          <DeleteIcon style={{ color: '#AEAEAE' }} />
                        </button>
                      )}
                    </>
                  )}
                </React.Fragment>
              )
            }
            openText={openTranslated}
            closeText={closeTranslated}
            filterOptions={(options, state) => {
              const results = defaultFilterOptions(options, state)
              return [
                ...results,
                <>
                  {isFlowWrite && (
                    <>
                      <StyledMttButtonPurple
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={handleAddNewGroup}
                        fullWidth
                        disabled={!botName}
                      >
                        {getTranslation('addGroup').trim()}
                      </StyledMttButtonPurple>
                    </>
                  )}
                </>,
              ]
            }}
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={getTranslation('selectGroup')}
                />
              </>
            )}
          />
        </AutoCompleteWrap>
      </BotAndGroupInfo>

      {!!botName && !currentData.groupName?.startsWith('flex') && (
        <ImportExportIcons>
          {state.isEditing && (
            <Tooltip title={getTranslation('tooltip.uploadGroup')}>
              <IconButton
                id="import-button"
                aria-label="import-file"
                onClick={() => importFileDefault()}
              >
                <CloudUpload />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={getTranslation('tooltip.downloadGroup')}>
            <IconButton
              aria-label="download-file"
              onClick={() => exportFileDefault()}
            >
              <CloudDownload />
            </IconButton>
          </Tooltip>
        </ImportExportIcons>
      )}
    </>
  )
}
export default RenderBots
